import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'react-hot-toast'
import { Card, Input, Button } from '@/shared/ui'
import { useAuth } from '@/shared/contexts'

const loginSchema = z.object({
  username: z.string().min(1, 'Логин обязателен'),
  password: z.string().min(1, 'Пароль обязателен'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login, user, isLoading } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const errorShownRef = useRef(false) // Для предотвращения дублирования в StrictMode

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  })

  // Редирект если уже авторизован
  useEffect(() => {
    if (user && !isLoading) {
      console.log('User is already authenticated, redirecting to /couriers')
      navigate({ to: '/couriers' })
    }
  }, [user, isLoading, navigate])

  const onSubmit = async (data: LoginFormData) => {
    console.log('Form submitted with data:', data)
    errorShownRef.current = false // Сбрасываем флаг перед новой попыткой
    try {
      setIsSubmitting(true)
      await login(data)
      // Редирект произойдет автоматически через useEffect выше
    } catch (error: any) {
      console.error('Login error:', error)
      // Показываем уведомление только один раз (защита от StrictMode)
      if (!errorShownRef.current) {
        toast.error(error.detail || 'Неверный логин или пароль', {
          id: 'login-error' // Уникальный ID для дополнительной защиты
        })
        errorShownRef.current = true
      }
      // Сбрасываем форму при ошибке
      reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Показываем загрузку при проверке авторизации
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          <span className="text-gray-500">Проверка авторизации...</span>
        </div>
      </div>
    )
  }

  // Если пользователь уже авторизован, не показываем форму входа
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          <span className="text-gray-500">Переход в админ панель...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Вход в админ панель
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Zveno Courier Management System
          </p>
        </div>
        
        <Card>
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <Input
              label="Логин"
              type="text"
              placeholder="Введите логин"
              error={errors.username?.message}
              {...register('username')}
            />
            
            <Input
              label="Пароль"
              type="password"
              placeholder="Введите пароль"
              error={errors.password?.message}
              {...register('password')}
            />
            
            <Button 
              type="submit" 
              className="w-full"
              size="lg"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Вход...' : 'Войти'}
            </Button>
          </form>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Для входа используйте учетные данные администратора
          </p>
        </div>
      </div>
    </div>
  )
}
